/**
 * Stream B — B2: Contact Table Design Upgrade ("Living Table")
 *
 * Design system applied:
 * - Row hover: bg-[var(--color-primary-light)] 100ms
 * - Hover reveals quick action buttons (Phone, Mail, MessageCircle) staggered 50ms
 * - Score: mini progress bar (green >70, yellow 40-70, red <40)
 * - Stage: color-coded badges per lifecycle
 * - Avatar: deterministic gradient from name hash
 * - Selected row: 3px left border primary + primary-light bg
 * - Skeleton: layout-matching (avatar circle + text lines)
 * - Content stagger: rows fade in 50ms apart
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import { normalizeCrmActionHref } from "@ease/crm/safe-action-hrefs";
import type { CrmContactSummarySignalTone } from "@ease/crm/contact-summary-signals";
import { Building2, Phone, Mail, MessageCircle, UserRound } from "lucide-react";
import { motion } from "framer-motion";
import type { ContactSummary } from "@/hooks/use-contacts";
import { Skeleton, SkeletonAvatar } from "@/components/skeleton";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

interface ContactTableProps {
  contacts: ContactSummary[];
  selectedId: string | null;
  selectedContactIds: string[];
  ownerLabelById?: ReadonlyMap<string, string>;
  onSelect: (id: string) => void;
  onToggleSelected: (id: string) => void;
  onToggleAllVisible: () => void;
  isAllVisibleSelected: boolean;
  isLoading: boolean;
}

export function ContactTable({
  contacts,
  selectedId,
  selectedContactIds,
  ownerLabelById,
  onSelect,
  onToggleSelected,
  onToggleAllVisible,
  isAllVisibleSelected,
  isLoading,
}: ContactTableProps) {
  if (isLoading && contacts.length === 0) {
    return <TableSkeleton />;
  }

  if (contacts.length === 0) {
    return null; // parent renders EmptyState
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] shadow-sm" data-reveal>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            <th className="w-12 px-4 py-3">
              <input
                type="checkbox"
                checked={isAllVisibleSelected}
                onChange={onToggleAllVisible}
                aria-label="Select all visible contacts"
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
              />
            </th>
            <th className="px-4 py-3">Contact</th>
            <th className="hidden px-4 py-3 xl:table-cell">Company</th>
            <th className="hidden px-4 py-3 md:table-cell">Score</th>
            <th className="hidden px-4 py-3 lg:table-cell">Stage</th>
            <th className="hidden px-4 py-3 lg:table-cell">Last Touch</th>
            <th className="hidden px-4 py-3 md:table-cell">Source</th>
            <th className="px-4 py-3">Tags</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {contacts.map((contact, idx) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              isSelected={selectedId === contact.id}
              isChecked={selectedContactIds.includes(contact.id)}
              ownerLabelById={ownerLabelById}
              onSelect={onSelect}
              onToggleSelected={onToggleSelected}
              staggerIndex={idx}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact Row — hover actions, selection border, stagger animation
// ---------------------------------------------------------------------------

function ContactRow({
  contact,
  isSelected,
  isChecked,
  ownerLabelById,
  onSelect,
  onToggleSelected,
  staggerIndex,
}: {
  contact: ContactSummary;
  isSelected: boolean;
  isChecked: boolean;
  ownerLabelById?: ReadonlyMap<string, string>;
  onSelect: (id: string) => void;
  onToggleSelected: (id: string) => void;
  staggerIndex: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isReducedMotion = useReducedMotion();
  const phoneHref = contact.phone ? normalizeCrmActionHref(`tel:${contact.phone}`) : null;
  const emailHref = contact.email ? normalizeCrmActionHref(`mailto:${contact.email}`) : null;
  const inboxSearchHref = buildInboxSearchHref(contact);
  const summarySignals = contact.crmSummarySignals?.slice(0, 5) ?? [];
  const displayName = contactDisplayName(contact);
  const assigneeLabel = contactAssigneeLabel(contact, ownerLabelById);

  return (
    <motion.tr
      initial={isReducedMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: isReducedMotion ? 0 : Math.min(staggerIndex * 0.05, 0.4), duration: isReducedMotion ? 0 : 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      onClick={() => onSelect(contact.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "stagger-item density-table-row cursor-pointer transition-colors duration-100",
        (isHovered || isChecked) && "bg-[var(--color-primary-light)]",
        isSelected && "bg-[var(--color-primary-light)] border-l-[3px] border-l-[var(--color-primary)]",
        !isSelected && !isHovered && !isChecked && "bg-[var(--color-background)]",
      )}
    >
      <td className="px-4 py-3 align-middle">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggleSelected(contact.id)}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Select ${contactDisplayName(contact)}`}
          className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <ContactAvatar firstName={contact.firstName} lastName={contact.lastName} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[var(--color-foreground)] truncate">
              {contactDisplayName(contact)}
            </p>
            <p className="text-xs text-[var(--color-muted-foreground)] truncate">{contact.email ?? "No email"}</p>
            {assigneeLabel ? (
              <p className="mt-0.5 inline-flex max-w-[14rem] items-center gap-1 truncate text-[11px] font-medium text-[var(--color-muted-foreground)]">
                <UserRound className="h-3 w-3 shrink-0" />
                <span className="truncate">Owner: {assigneeLabel}</span>
              </p>
            ) : null}
            {summarySignals.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {summarySignals.map((signal) => (
                  <span
                    key={signal.key}
                    title={signal.detail ?? undefined}
                    className={cn(
                      "inline-flex max-w-[12rem] items-center gap-1 truncate rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                      getContactSummarySignalClasses(signal.tone),
                    )}
                  >
                    <span className="truncate">{signal.label}</span>
                    <span className="text-[var(--color-muted-foreground)]">·</span>
                    <span className="shrink-0">{signal.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Quick action buttons — fade in on hover */}
          <div className={cn("flex items-center gap-1 transition-opacity duration-150", isHovered ? "opacity-100" : "opacity-0")}>
            {phoneHref ? (
              <motion.a
                href={phoneHref}
                onClick={(e) => e.stopPropagation()}
                initial={isReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ delay: 0, duration: isReducedMotion ? 0 : undefined }}
                aria-label={`Call ${displayName}`}
                title={`Call ${displayName}`}
                className="rounded-md p-1.5 hover:bg-[var(--color-primary)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]"
              >
                <Phone className="h-3.5 w-3.5" />
              </motion.a>
            ) : null}
            {emailHref ? (
              <motion.a
                href={emailHref}
                onClick={(e) => e.stopPropagation()}
                initial={isReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ delay: isReducedMotion ? 0 : 0.05, duration: isReducedMotion ? 0 : undefined }}
                aria-label={`Email ${displayName}`}
                title={`Email ${displayName}`}
                className="rounded-md p-1.5 hover:bg-[var(--color-primary)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]"
              >
                <Mail className="h-3.5 w-3.5" />
              </motion.a>
            ) : null}
            <motion.a
              href={inboxSearchHref}
              onClick={(e) => e.stopPropagation()}
              initial={isReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ delay: isReducedMotion ? 0 : 0.1, duration: isReducedMotion ? 0 : undefined }}
              aria-label={`Open conversations for ${displayName}`}
              title={`Open conversations for ${displayName}`}
              className="rounded-md p-1.5 hover:bg-[var(--color-primary)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]"
            >
              <MessageCircle className="h-3.5 w-3.5" />
            </motion.a>
          </div>
        </div>
      </td>
      <td className="hidden px-4 py-3 text-[var(--color-muted-foreground)] xl:table-cell">
        <CompanyCell company={contact.company} />
      </td>
      <td className="hidden px-4 py-3 md:table-cell">
        <ScoreBar score={contact.leadScore} engagementStatus={contact.engagementStatus} />
      </td>
      <td className="hidden px-4 py-3 lg:table-cell">
        <StageBadge stage={contact.lifecycleStage} />
      </td>
      <td className="hidden px-4 py-3 text-[var(--color-muted-foreground)] lg:table-cell">
        {contact.lastActivityAt ? relativeTime(contact.lastActivityAt) : "Never"}
      </td>
      <td className="hidden px-4 py-3 text-[var(--color-muted-foreground)] md:table-cell">
        {contact.source ?? "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {contact.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-xs text-[var(--color-primary)]">
              {tag}
            </span>
          ))}
          {contact.tags.length > 2 && (
            <span className="text-xs text-[var(--color-muted-foreground)]">+{contact.tags.length - 2}</span>
          )}
        </div>
      </td>
    </motion.tr>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Avatar with deterministic gradient from name hash */
function ContactAvatar({ firstName, lastName }: { firstName: string | null; lastName: string | null }) {
  const initials = `${(firstName ?? "?")[0]}${(lastName ?? "")[0] || ""}`.toUpperCase();
  const hash = nameHash(`${firstName ?? ""}${lastName ?? ""}`);
  const hue1 = hash % 360;
  const hue2 = (hash * 7) % 360;

  return (
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
      style={{ background: `linear-gradient(135deg, hsl(${hue1}, 60%, 55%), hsl(${hue2}, 50%, 45%))` }}
    >
      {initials}
    </div>
  );
}

/** Score as mini triage summary with accessible progress semantics. */
function ScoreBar({ score, engagementStatus }: { score: number | null; engagementStatus: string | null }) {
  if (score == null) return <span className="text-[var(--color-muted-foreground)]">—</span>;
  const boundedScore = Math.max(0, Math.min(100, Math.round(score)));
  const color = boundedScore >= 70 ? "var(--color-success)" : boundedScore >= 40 ? "var(--color-warning)" : "var(--color-error)";
  const tier = formatLeadScoreTier(boundedScore);
  const engagementLabel = formatEngagementStatus(engagementStatus);

  return (
    <div className="min-w-[7rem]">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold" style={{ color }}>{boundedScore}</span>
        <span className="rounded-full bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-muted-foreground)]">
          {tier}
        </span>
      </div>
      <div
        className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-[var(--color-surface)]"
        role="progressbar"
        aria-label={`Lead score ${boundedScore} of 100`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={boundedScore}
      >
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${boundedScore}%`, backgroundColor: color }} />
      </div>
      {engagementLabel ? (
        <p className="mt-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">{engagementLabel}</p>
      ) : null}
    </div>
  );
}

function formatLeadScoreTier(score: number): string {
  if (score >= 70) return "Hot";
  if (score >= 40) return "Warm";
  return "Cold";
}

function formatEngagementStatus(status: string | null): string | null {
  const normalized = status?.trim();
  if (!normalized) return null;
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function CompanyCell({ company }: { company: string | null }) {
  if (!company) {
    return <span>—</span>;
  }

  return (
    <span className="inline-flex max-w-[11rem] items-center gap-1.5 truncate rounded-full bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-foreground)]">
      <Building2 className="h-3 w-3 shrink-0 text-[var(--color-muted-foreground)]" />
      <span className="truncate">{company}</span>
    </span>
  );
}

/** Stage badge with lifecycle color coding */
function StageBadge({ stage }: { stage: string | null }) {
  if (!stage) return <span className="text-[var(--color-muted-foreground)]">—</span>;
  const styles: Record<string, string> = {
    lead: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
    subscriber: "bg-[var(--color-info)]/10 text-[var(--color-info)]",
    opportunity: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]",
    customer: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    evangelist: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  };
  const labels: Record<string, string> = {
    lead: "Lead", subscriber: "Subscriber", opportunity: "Opportunity",
    customer: "Customer", evangelist: "Evangelist", other: "Other",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", styles[stage] ?? "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]")}>
      {labels[stage] ?? stage}
    </span>
  );
}

function getContactSummarySignalClasses(tone: CrmContactSummarySignalTone): string {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted-foreground)]";
}

/** Layout-matching skeleton with avatar circles + text placeholders */
function TableSkeleton() {
  return (
    <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
      {/* Header skeleton */}
      <div className="flex gap-4 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
        {["w-24", "w-16", "w-16", "w-20", "w-16", "w-16"].map((w, i) => (
          <Skeleton key={i} className={`h-3 ${w}`} />
        ))}
      </div>
      {/* Row skeletons */}
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-3">
          <SkeletonAvatar />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2.5 w-48" />
          </div>
          <Skeleton className="hidden h-1.5 w-10 rounded-full md:block" />
          <Skeleton className="hidden h-5 w-16 rounded-full lg:block" />
          <Skeleton className="hidden h-3 w-12 lg:block" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function contactDisplayName(c: ContactSummary): string {
  const parts = [c.firstName, c.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : c.email ?? "Unknown";
}

function contactAssigneeLabel(c: ContactSummary, ownerLabelById?: ReadonlyMap<string, string>): string | null {
  const summaryLabel = c.assignedToLabel?.trim();
  if (summaryLabel) {
    return summaryLabel;
  }

  const assignedTo = c.assignedTo?.trim();
  if (!assignedTo) {
    return null;
  }

  return ownerLabelById?.get(assignedTo) ?? assignedTo;
}

function buildInboxSearchHref(contact: ContactSummary): string {
  const displayName = contactDisplayName(contact);
  const query = contact.email ?? (displayName === "Unknown" ? null : displayName);
  if (!query) {
    return "/inbox";
  }

  return `/inbox?${new URLSearchParams({ search: query }).toString()}`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

/** Simple string hash for deterministic avatar gradients */
function nameHash(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}
